import * as Sentry from '@sentry/react';
import { Integration, StackFrame } from '@sentry/types';
import { ExtraErrorData as ExtraErrorDataIntegration } from '@sentry/integrations';
import { Config } from '../config';

export const ignoredErrors = new RegExp(
  `TypeError: NetworkError when attempting to fetch resource|TypeError: Failed to fetch|TypeError: Load failed|500 Internal Server Error|401 Unauthorized|403 Forbidden|403 FAILED REQUEST|404 Not Found|502 Bad Gateway|503 Service Unavailable|cannot fetch the geo location based on IP|Object Not Found Matching Id:2`
);

export const beforeSendFilter = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null => {
  /**
   * BIG FAT NOTE:
   * I have added today a list of ip-addresses that need to be ignored by Sentry for the widget project only.
   * It is a range of ip-addresses that belong to Hetzner, famous for crawlers and such.
   * ref: https://brenger.sentry.io/issues/4631778387/?project=5620968&query=is%3Aunresolved&referrer=issue-stream&statsPeriod=14d&stream_index=4
   * manage ip address at "widget" project settings > Inbound filters
   */
  /**
   * Usually these rejections flag why it is unhandled. Think of "value: Timeout" or "failed with keys: keyslist".
   * In sentry we suddenly experiencing some without any value (all windows 10, chrome), suspecting it is a bug in the browser
   * So this removes the noise.
   */
  const error = hint?.originalException;
  const errorMessage = (typeof error === 'string' ? error : (error as Error)?.message || '').trim();
  const nonInformativeMessage = 'Non-Error promise rejection captured with value:';
  if (errorMessage && errorMessage.substr(-nonInformativeMessage.length) === nonInformativeMessage) {
    return null;
  }
  return event;
};

/**
 * iOS WebViews attribute stack-overflow frames to the HTML document instead of brenger-main.js,
 * so Sentry finds no artifact to match them against and the whole RangeError family stays
 * minified. Point those frames back at the bundle and re-attach the debug id that sentry-cli
 * injected at build time. The SDK applies debug_meta *after* event processors precisely so that
 * RewriteFrames can rename files without breaking the filename -> debug ID relationship.
 */
const BUNDLE_URL = new URL('brenger-main.js', new URL(import.meta.env.BASE_URL, window.location.href)).href;

const BUNDLE_DEBUG_ID = ((): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const map = (window as unknown as { _sentryDebugIds?: Record<string, string> })._sentryDebugIds;
  const ids = new Set(Object.values(map || {}));
  return ids.size === 1 ? ids.values().next().value : undefined;
})();

const isMisattributedBundleFrame = (frame: StackFrame): boolean =>
  !!frame.filename &&
  // One of our own HTML pages: every script we ship is served from another host.
  frame.filename.startsWith(window.location.origin) &&
  !/\.[cm]?js($|[?#])/.test(frame.filename) &&
  // A minified symbol. The page's own inline scripts (GTM, consent mode) have readable names.
  /^[A-Za-z$_][\w$]{0,2}$/.test(frame.function || '');

export const initSentry = (): void => {
  if (!['production', 'staging'].includes(Config.NODE_ENV || '')) {
    return;
  }

  Sentry.init({
    environment: Config.NODE_ENV,
    dsn: Config.SENTRY_DSN,
    release: Config.COMMIT_SHA,
    allowUrls: [/(brenger)(-transport)?\.(nl|be|com)/, 'netlify.app'], // Only track exceptions from files that are served from this domain
    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: url => !url.includes('stp.brenger.nl'),
      }),
      Sentry.rewriteFramesIntegration({
        iteratee: frame =>
          isMisattributedBundleFrame(frame) ? { ...frame, filename: BUNDLE_URL, debug_id: BUNDLE_DEBUG_ID } : frame,
      }),
      new ExtraErrorDataIntegration({
        // limit of how deep the object serializer should go. Anything deeper than limit will
        // be replaced with standard Node.js REPL notation of [Object], [Array], [Function] or
        // a primitive value. Defaults to 3.
        depth: 4,
      }) as Integration,
      Sentry.feedbackIntegration({
        autoInject: false,
      }),
    ],
    tracePropagationTargets: [/^https:\/\/(api|pricing|geo)\.brenger\.nl/],
    tracesSampleRate: 1.0, // lower this to consume a percentage of transactions
    normalizeDepth: 7,
    maxValueLength: 5000,
    ignoreErrors: [
      ignoredErrors,
      'hj.hq is not a function',
      'Could not load "onion".',
      'jQuery is not defined',
      // Attempt to ignore freshchat
      'with keys: data, status, success',
      // Error occurs with tag-manager, probably by a client script,
      // Or this is the reason but no solution yet: https://github.com/getsentry/sentry-javascript/issues/9110
      "undefined is not an object (evaluating 'a.L')",
      'Ongeldig argument.', // Error Occurs When Microsoft users Try to Import a Solution into Dynamics 365
      "/Cannot read property '_avast_submit' of undefined/g",
      'parentRef.postMessage is not a function',
      /hj.hq/,
      /fbq/,
      'QuotaExceededError: QuotaExceededError',
      "Argument 1 ('element') to Window.getComputedStyle must be an instance of Element",
      'QuotaExceededError: The quota has been exceeded.',
      /initFreshChat/,
      'geannuleerd',
      'Abgebrochen',
      'cancelled',
      // Browser extension (Safari/DuckDuckGo) content scripts calling runtime.sendMessage()
      // when the extension background is unavailable. Not caused by Brenger code.
      'Invalid call to runtime.sendMessage()',
      'SecurityError: Blocked a frame with origin "https://www.brenger.nl" from accessing a cross-origin frame',
    ],
    denyUrls: [
      /api\.claydar\.com/,
      /script\.hotjar\.com/,
      /ct\.pinterest\.com/,
      /wchat\.freshchat\.com/,
      /maps\.googleapis\.com/,
      /\/subscribe_newsletter/,
    ],
    beforeSend: beforeSendFilter,
  });
  Object.assign(globalThis, { Sentry });
};

type SentryContextData = Record<string, unknown>;

const isContextObject = (value: unknown): value is SentryContextData =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export type SentryContextKey =
  | 'widget_pickup'
  | 'widget_delivery'
  | 'contact_pickup'
  | 'contact_delivery'
  | 'flow_facts'
  | 'user'
  | 'locality_change';

export const setSentryContext = (key: SentryContextKey, data: SentryContextData | null): void => {
  const scope = Sentry.getIsolationScope();

  if (data === null) {
    scope.setContext(key, null);
    return;
  }

  const existingContext = scope.getScopeData().contexts[key];
  const existing = isContextObject(existingContext) ? existingContext : {};

  scope.setContext(key, { ...existing, ...data });
};
